import type { StatTypeId } from './types'

export interface StatTypeDef {
  id: StatTypeId
  /** Full name, used in stats tables and the event log. */
  label: string
  /** Short name for the live grid button. */
  short: string
  /** Accent colour for the button and charts. */
  color: string
  /** Enabled for a brand-new season. v1 ships rebound / assist / steal. */
  defaultEnabled: boolean
}

/**
 * The full registry. Adding a stat type here makes it available to toggle on in
 * Manage without any schema change -- events already store an arbitrary statType.
 */
export const STAT_TYPES: StatTypeDef[] = [
  { id: 'reb', label: 'Rebound', short: 'REB', color: '#f97316', defaultEnabled: true },
  { id: 'ast', label: 'Assist', short: 'AST', color: '#38bdf8', defaultEnabled: true },
  { id: 'stl', label: 'Steal', short: 'STL', color: '#22c55e', defaultEnabled: true },
  { id: 'to', label: 'Turnover', short: 'TO', color: '#ef4444', defaultEnabled: false },
  { id: 'blk', label: 'Block', short: 'BLK', color: '#a855f7', defaultEnabled: false },
  { id: 'oreb', label: 'Off. Rebound', short: 'OREB', color: '#fb923c', defaultEnabled: false },
  { id: 'dreb', label: 'Def. Rebound', short: 'DREB', color: '#fdba74', defaultEnabled: false },
]

const BY_ID = new Map(STAT_TYPES.map((s) => [s.id, s]))

export function statType(id: StatTypeId): StatTypeDef {
  const found = BY_ID.get(id)
  if (found) return found
  // Unknown ids can only arrive from an import made by a newer build. Degrade
  // gracefully rather than crashing the live grid.
  return { id, label: id.toUpperCase(), short: id.toUpperCase(), color: '#94a3b8', defaultEnabled: false }
}

export function defaultEnabledStatTypes(): StatTypeId[] {
  return STAT_TYPES.filter((s) => s.defaultEnabled).map((s) => s.id)
}

/** Keeps enabled stat types in registry order regardless of how they were stored. */
export function orderStatTypes(ids: StatTypeId[]): StatTypeId[] {
  const set = new Set(ids)
  const known = STAT_TYPES.filter((s) => set.has(s.id)).map((s) => s.id)
  const unknown = ids.filter((id) => !BY_ID.has(id))
  return [...known, ...unknown]
}
